/**
 * PatientFiles — Premium "Imaging & Documents" workspace.
 *
 * Full redesign inspired by modern medical imaging tools (Apple / Notion feel):
 *   - Premium summary header with per-category counts + thin storage progress bar
 *   - Upload dropdown with typed presets (Clinical Photo / X-Ray / DICOM / TIFF / PDF / Other)
 *   - Filter pill row: All / Images / DICOM / TIFF / PDF / Recent / Favourites + inline search
 *   - Card gallery with hover elevation, type badge, favourite star, per-card meta
 *   - Elegant empty state with illustration
 *   - Preview drawer per file type — images (zoom / rotate), PDF (native), DICOM
 *     (future-viewer placeholder with metadata + Download Original)
 *   - Metadata panel drawer with filename, upload date, doctor, patient, size,
 *     type, resolution (parsed client-side for images), and DICOM meta placeholder
 *   - Compare X-rays flow preserved (from previous version)
 *
 * Favourites are persisted client-side in localStorage under
 * `viyora:favs:${patientId}` — no backend change required.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Upload, Image as ImageIcon, FileText, Trash2, Download, X, Loader2,
  ZoomIn, ZoomOut, RotateCw, Maximize2, Check, ArrowLeftRight, ChevronDown,
  Search, Star, ScanLine, FileArchive, Info, Sparkles, Camera, Layers,
  Pause, Play, XCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useChunkedUploader } from "@/lib/useChunkedUploader";

/* ---------- formatting helpers ---------- */
const kb = (n = 0) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/* ---------- file-type classification ---------- */
function classify(f) {
  const ct = (f.content_type || "").toLowerCase();
  const name = (f.original_filename || "").toLowerCase();
  if (ct === "application/dicom" || name.endsWith(".dcm") || name.endsWith(".dicomdir")) return "dicom";
  if (ct === "image/tiff" || name.endsWith(".tif") || name.endsWith(".tiff")) return "tiff";
  if (ct.startsWith("image/heic") || ct.startsWith("image/heif") || name.endsWith(".heic") || name.endsWith(".heif")) return "heic";
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".stl") || ct === "model/stl") return "stl";
  if (ct === "application/zip" || name.endsWith(".zip")) return "zip";
  return "other";
}

const TYPE_META = {
  image: { label: "IMAGE",  icon: ImageIcon,  chip: "bg-[#E8F0EB] text-[#2D6A4F]", browserPreview: true },
  tiff:  { label: "TIFF",   icon: ScanLine,   chip: "bg-[#E8ECF3] text-[#28527A]", browserPreview: false },
  heic:  { label: "HEIC",   icon: ImageIcon,  chip: "bg-[#F0EAE0] text-[#8A6A2E]", browserPreview: false },
  dicom: { label: "DICOM",  icon: Layers,     chip: "bg-[#F0E8F3] text-[#5E337A]", browserPreview: false },
  pdf:   { label: "PDF",    icon: FileText,   chip: "bg-[#FDECE4] text-[#8A3F1E]", browserPreview: true },
  stl:   { label: "STL",    icon: FileArchive,chip: "bg-[#EAEAF3] text-[#3D3E7A]", browserPreview: false },
  zip:   { label: "ZIP",    icon: FileArchive,chip: "bg-[#EFEFEF] text-#4A4A4A", browserPreview: false },
  other: { label: "FILE",   icon: FileText,   chip: "bg-[#EFEFEF] text-[#4A4A4A]", browserPreview: false },
};

const FILTER_TABS = [
  { id: "all",       label: "All",        match: () => true },
  { id: "images",    label: "Images",     match: (f) => ["image", "heic"].includes(f._type) },
  { id: "dicom",     label: "DICOM",      match: (f) => f._type === "dicom" },
  { id: "tiff",      label: "TIFF",       match: (f) => f._type === "tiff" },
  { id: "pdf",       label: "PDF",        match: (f) => f._type === "pdf" },
  { id: "recent",    label: "Recent",     match: (f) => (Date.now() - new Date(f.created_at).getTime()) < 7 * 24 * 3600 * 1000 },
  { id: "favourites",label: "Favourites", match: (f, favSet) => favSet.has(f.id) },
];

const UPLOAD_PRESETS = [
  { id: "photo",    label: "Clinical Photo", kind: "photo",    icon: Camera,    accept: "image/jpeg,image/png,image/webp,image/heic,image/heif" },
  { id: "xray",     label: "X-Ray",          kind: "xray",     icon: ImageIcon, accept: "image/*" },
  { id: "dicom",    label: "DICOM Study",    kind: "xray",     icon: Layers,    accept: ".dcm,.dicomdir,application/dicom" },
  { id: "tiff",     label: "TIFF Image",     kind: "xray",     icon: ScanLine,  accept: ".tif,.tiff,image/tiff" },
  { id: "pdf",      label: "PDF",            kind: "document", icon: FileText,  accept: "application/pdf" },
  { id: "other",    label: "Other File",     kind: "document", icon: FileArchive, accept: "*/*" },
];


export default function PatientFiles({ patientId, patientName }) {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadMenu, setUploadMenu] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [q, setQ] = useState("");
  const [viewer, setViewer] = useState(null);
  const [meta, setMeta] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [compareView, setCompareView] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [favs, setFavs] = useState(() => {
    try {
      const raw = localStorage.getItem(`viyora:favs:${patientId}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const fileInputRef = useRef(null);
  const pendingPresetRef = useRef(null);
  const menuRef = useRef(null);
  const dragCounterRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: filesData }, { data: statsData }] = await Promise.all([
        api.get(`/patients/${patientId}/files`),
        api.get(`/patients/${patientId}/files/stats`).catch(() => ({ data: null })),
      ]);
      setFiles((filesData || []).map((f) => ({ ...f, _type: classify(f) })));
      setStats(statsData);
    } catch { /* toast handled by axios interceptor */ }
    finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const uploader = useChunkedUploader({
    patientId,
    onCompleted: () => { load(); },
  });

  // Close upload menu on outside click
  useEffect(() => {
    if (!uploadMenu) return;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setUploadMenu(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [uploadMenu]);

  const persistFavs = (next) => {
    try { localStorage.setItem(`viyora:favs:${patientId}`, JSON.stringify([...next])); } catch { /* ignore */ }
  };
  const toggleFav = (fid) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      persistFavs(next);
      return next;
    });
  };

  const imageFiles = useMemo(() => files.filter((f) => ["image", "heic"].includes(f._type)), [files]);

  const visible = useMemo(() => {
    const tab = FILTER_TABS.find((t) => t.id === activeFilter);
    const query = q.trim().toLowerCase();
    return files
      .filter((f) => tab.match(f, favs))
      .filter((f) => !query || (f.original_filename || "").toLowerCase().includes(query));
  }, [files, activeFilter, q, favs]);

  const grouped = useMemo(() => {
    const g = {};
    for (const f of visible) {
      const key = new Date(f.created_at).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
      (g[key] ||= []).push(f);
    }
    return g;
  }, [visible]);

  const pickPreset = (preset) => {
    pendingPresetRef.current = preset;
    fileInputRef.current.accept = preset.accept;
    fileInputRef.current.multiple = true;
    fileInputRef.current.click();
    setUploadMenu(false);
  };

  const onFileChosen = (e) => {
    const list = e.target.files;
    if (!list || !list.length) return;
    // Snapshot the FileList BEFORE clearing the input — `list` is a live
    // reference to `e.target.files`, so setting `e.target.value = ""` empties
    // it. That is what caused the earlier "0 file queued" regression.
    const arr = Array.from(list);
    e.target.value = "";
    const preset = pendingPresetRef.current || UPLOAD_PRESETS[5];
    // Guess kind per file — DICOM/TIFF are always X-rays, PDFs are documents.
    const inferKind = (f) => {
      const n = (f.name || "").toLowerCase();
      if (n.endsWith(".pdf")) return "document";
      if (n.endsWith(".dcm") || n.endsWith(".dicomdir") || n.endsWith(".tif") || n.endsWith(".tiff")) return "xray";
      return preset.kind;
    };
    // Group by kind so we call enqueue only once per kind (progress order stable)
    const byKind = {};
    for (const f of arr) { (byKind[inferKind(f)] ||= []).push(f); }
    for (const [kind, fs] of Object.entries(byKind)) {
      uploader.enqueue(fs, kind);
    }
    toast.success(`${arr.length} file${arr.length > 1 ? "s" : ""} queued`);
    pendingPresetRef.current = null;
  };

  /* --- Drag-and-drop --- */
  const onDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!(e.dataTransfer?.types || []).includes("Files")) return;
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);
    const list = e.dataTransfer?.files;
    if (!list || !list.length) return;
    // Auto-detect kind per file.
    const arr = Array.from(list);
    const inferKind = (f) => {
      const n = (f.name || "").toLowerCase();
      if (n.endsWith(".pdf")) return "document";
      if (n.endsWith(".dcm") || n.endsWith(".dicomdir") || n.endsWith(".tif") || n.endsWith(".tiff")) return "xray";
      if (n.match(/\.(jpg|jpeg|png|webp|heic|heif)$/)) return "xray";
      return "document";
    };
    const byKind = {};
    for (const f of arr) { (byKind[inferKind(f)] ||= []).push(f); }
    for (const [kind, fs] of Object.entries(byKind)) uploader.enqueue(fs, kind);
    toast.success(`${arr.length} file${arr.length > 1 ? "s" : ""} added to queue`);
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete this file? This action cannot be undone.")) return;
    try {
      await api.delete(`/files/${fid}`);
      toast.success("Deleted");
      setSelected((s) => s.filter((id) => id !== fid));
      setFavs((prev) => { const next = new Set(prev); next.delete(fid); persistFavs(next); return next; });
      load();
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    }
  };

  const openFile = async (f) => {
    if (compareMode && ["image", "heic"].includes(f._type)) {
      setSelected((s) => s.includes(f.id) ? s.filter((x) => x !== f.id) : [...s, f.id].slice(-4));
      return;
    }
    try {
      const resp = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setViewer({ url, file: f });
    } catch { toast.error("Could not open file"); }
  };

  const startCompare = async () => {
    if (selected.length < 2) { toast.error("Pick 2 or more images to compare"); return; }
    try {
      const chosen = imageFiles.filter((f) => selected.includes(f.id))
                               .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const resps = await Promise.all(chosen.map((f) => api.get(`/files/${f.id}/download`, { responseType: "blob" })));
      setCompareView(resps.map((r, i) => ({ file: chosen[i], url: URL.createObjectURL(r.data) })));
    } catch { toast.error("Compare failed"); }
  };

  /* ---------- header stats ---------- */
  const clinicUsed = stats?.clinic_storage_used_bytes ?? 0;
  const clinicLimit = stats?.clinic_storage_limit_bytes ?? 0;
  const storagePct = clinicLimit > 0 ? Math.min(100, (clinicUsed / clinicLimit) * 100) : 0;
  const counts = stats?.counts || {};
  const sizeByType = stats?.size_by_type_bytes || {};

  const activeJobs = uploader.jobs.filter((j) => j.status !== "completed" && j.status !== "aborted");
  const isUploading = activeJobs.some((j) => j.status === "uploading" || j.status === "queued");

  return (
    <div
      data-testid="patient-files"
      className="space-y-6 relative"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Drag-drop overlay */}
      {dragOver && (
        <div
          data-testid="drag-overlay"
          className="fixed inset-0 z-40 bg-pine/70 backdrop-blur-sm grid place-items-center animate-in fade-in-0 pointer-events-none"
        >
          <div className="rounded-3xl bg-white/95 border border-white/30 px-10 py-8 text-center shadow-2xl">
            <Upload className="w-10 h-10 text-ink mx-auto mb-3" />
            <div className="font-display text-2xl text-ink">Drop to upload</div>
            <div className="text-xs text-ink-mid mt-1">DICOM · TIFF · Images · PDF · STL — any size</div>
          </div>
        </div>
      )}
      {/* ================= Premium summary header ================= */}
      <div className="rounded-3xl bg-white border border-[#EFEADC] p-6 md:p-8" data-testid="pf-header">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline text-ink-soft mb-1">Patient imaging</div>
            <h2 className="font-display text-3xl tracking-tight text-ink">Imaging &amp; Documents</h2>
            <p className="text-xs text-ink-mid mt-2 max-w-md">
              A calm home for every X-ray, DICOM study, TIFF scan, clinical photo and report on this patient&rsquo;s record.
            </p>
          </div>

          <div className="flex items-center gap-2 relative" ref={menuRef}>
            {imageFiles.length >= 2 && !isUploading && (
              <button
                data-testid="btn-toggle-compare"
                onClick={() => { setCompareMode((v) => !v); setSelected([]); }}
                className={`text-xs inline-flex items-center gap-2 px-3 py-2 rounded-full border transition ${compareMode ? "bg-pine text-linen border-pine" : "border-[#E8EAE9] hover:bg-linen"}`}
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                {compareMode ? `${selected.length} selected` : "Compare X-rays"}
              </button>
            )}
            {compareMode && selected.length >= 2 && (
              <button onClick={startCompare} data-testid="btn-do-compare" className="btn-primary text-xs inline-flex items-center gap-2">
                <Check className="w-3.5 h-3.5" /> Compare
              </button>
            )}
            <div className="relative">
              <button
                data-testid="btn-upload-menu"
                onClick={() => setUploadMenu((v) => !v)}
                className="btn-primary text-xs inline-flex items-center gap-2"
              >
                {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {isUploading ? `Uploading ${activeJobs.length}` : "Upload"}
                <ChevronDown className="w-3.5 h-3.5 -mr-0.5" />
              </button>
              {uploadMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-[#E8EAE9] rounded-2xl shadow-xl overflow-hidden z-30 animate-in fade-in-0 slide-in-from-top-1" data-testid="upload-menu">
                  <div className="px-4 py-2.5 border-b border-[#F0EBE0] text-[10px] uppercase tracking-widest text-ink-soft">
                    Choose file type
                  </div>
                  {UPLOAD_PRESETS.map((p) => {
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`upload-preset-${p.id}`}
                        onClick={() => pickPreset(p)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-linen/70 inline-flex items-center gap-3 border-b border-[#F5F1E5] last:border-b-0"
                      >
                        <span className="w-8 h-8 rounded-full bg-linen/70 grid place-items-center text-ink">
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-ink/80">{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={onFileChosen}
                className="hidden"
                data-testid="file-input"
              />
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 border-t border-[#F0EBE0] pt-5">
          <StatCell label="Images" value={counts.images ?? 0} sub={kb(sizeByType.images ?? 0)} testid="stat-images" />
          <StatCell label="DICOM studies" value={counts.dicom ?? 0} sub={kb(sizeByType.dicom ?? 0)} testid="stat-dicom" />
          <StatCell label="Documents" value={(counts.pdf ?? 0) + (counts.other ?? 0)} sub={kb((sizeByType.pdf ?? 0) + (sizeByType.other ?? 0))} testid="stat-docs" />
          <div data-testid="stat-storage">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Storage used</div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-2xl text-ink tabular-nums">{kb(clinicUsed)}</span>
              <span className="text-[10px] text-ink-soft">/ {kb(clinicLimit)}</span>
            </div>
            <div className="h-[3px] bg-[#F0EBE0] rounded-full mt-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${storagePct > 85 ? "bg-[#C05C4A]" : "bg-pine"}`}
                style={{ width: `${storagePct}%` }}
                data-testid="storage-progress"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================= Upload queue ================= */}
      {uploader.jobs.length > 0 && (
        <UploadQueue jobs={uploader.jobs} api={uploader} />
      )}

      {/* ================= Filter row + search ================= */}
      {files.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap" data-testid="pf-filters">
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTER_TABS.map((t) => {
              const isActive = activeFilter === t.id;
              const count = t.id === "all" ? files.length : files.filter((f) => t.match(f, favs)).length;
              return (
                <button
                  key={t.id}
                  data-testid={`filter-${t.id}`}
                  onClick={() => setActiveFilter(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${
                    isActive ? "bg-pine text-linen" : "bg-white border border-[#E8EAE9] text-ink-mid hover:bg-linen/70"
                  }`}
                >
                  {t.id === "favourites" && <Star className="w-3 h-3" />}
                  {t.label}
                  <span className={`text-[10px] ${isActive ? "text-linen/70" : "text-ink-soft"}`}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              data-testid="pf-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search filename…"
              className="w-full pl-9 pr-3 py-1.5 rounded-full border border-[#E8EAE9] text-xs bg-white"
            />
          </div>
        </div>
      )}

      {/* ================= Gallery ================= */}
      {loading ? (
        <div className="py-16 text-center text-ink-soft text-sm inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading files…
        </div>
      ) : files.length === 0 ? (
        <EmptyState onUpload={() => setUploadMenu(true)} />
      ) : visible.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#E8EAE9] py-14 text-center text-ink-soft text-sm">
          No files match this filter.
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([bucket, items]) => (
            <div key={bucket} data-testid={`pf-bucket-${bucket.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="overline text-ink-soft">{bucket}</div>
                <div className="h-px bg-[#F0EBE0] flex-1" />
                <div className="text-[10px] text-ink-soft">{items.length} file{items.length > 1 ? "s" : ""}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((f) => (
                  <FileCard
                    key={f.id}
                    f={f}
                    onOpen={() => openFile(f)}
                    onDelete={() => remove(f.id)}
                    onInfo={() => setMeta(f)}
                    onToggleFav={() => toggleFav(f.id)}
                    isFav={favs.has(f.id)}
                    compareMode={compareMode}
                    isSelected={selected.includes(f.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Viewer / drawer / compare */}
      {viewer && (
        <FileViewer
          viewer={viewer}
          onClose={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }}
          onInfo={() => { setMeta(viewer.file); }}
        />
      )}
      {meta && (
        <MetaDrawer
          file={meta}
          patientName={patientName}
          onClose={() => setMeta(null)}
        />
      )}
      {compareView && (
        <CompareViewer
          items={compareView}
          onClose={() => {
            compareView.forEach((it) => URL.revokeObjectURL(it.url));
            setCompareView(null); setCompareMode(false); setSelected([]);
          }}
        />
      )}
    </div>
  );
}


/* ============================================================================ */
/* UploadQueue — visible progress panel for in-flight and finished uploads      */
/* ============================================================================ */

const JOB_STATUS_STYLE = {
  queued:     "bg-linen text-ink",
  uploading:  "bg-[#E8EEF3] text-[#28527A]",
  paused:     "bg-[#F0EBE0] text-[#8A6A2E]",
  completed:  "bg-[#E8F0EB] text-[#2D6A4F]",
  aborted:    "bg-[#F5E7E4] text-[#8A2D2D]",
  error:      "bg-[#FDECEB] text-[#B94A48]",
};

function UploadQueue({ jobs, api: q }) {
  const anyDone = jobs.some((j) => j.status === "completed" || j.status === "aborted");
  return (
    <div className="rounded-2xl bg-white border border-[#EFEADC] overflow-hidden" data-testid="upload-queue">
      <div className="px-4 py-3 border-b border-[#F0EBE0] flex items-center justify-between">
        <div>
          <div className="overline text-ink-soft">Uploads</div>
          <div className="text-xs text-ink-mid mt-0.5">
            {jobs.filter((j) => j.status === "uploading").length} active ·{" "}
            {jobs.filter((j) => j.status === "completed").length} done
            {jobs.some((j) => j.status === "error") && (
              <span className="text-[#B94A48]"> · {jobs.filter((j) => j.status === "error").length} failed</span>
            )}
          </div>
        </div>
        {anyDone && (
          <button
            data-testid="clear-finished"
            onClick={q.clearFinished}
            className="text-[11px] px-3 py-1.5 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen"
          >
            Clear finished
          </button>
        )}
      </div>
      <div className="divide-y divide-[#F5F1E5]">
        {jobs.map((j) => (
          <UploadRow key={j.id} job={j} api={q} />
        ))}
      </div>
    </div>
  );
}


function UploadRow({ job, api: q }) {
  const pct = Math.round((job.progress || 0) * 100);
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`upload-row-${job.id}`}>
      <div className="w-9 h-9 rounded-full bg-linen/70 grid place-items-center text-ink-mid shrink-0">
        {job.status === "completed" ? <Check className="w-4 h-4 text-[#2D6A4F]" /> :
         job.status === "error" ? <XCircle className="w-4 h-4 text-[#B94A48]" /> :
         job.status === "paused" ? <Pause className="w-4 h-4" /> :
         <Loader2 className={`w-4 h-4 ${job.status === "uploading" ? "animate-spin" : ""}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-ink truncate" title={job.filename}>{job.filename}</div>
          <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${JOB_STATUS_STYLE[job.status]}`}>{job.status}</span>
        </div>
        <div className="mt-1 h-[3px] bg-[#F0EBE0] rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${job.status === "error" ? "bg-[#B94A48]" : "bg-pine"}`}
            style={{ width: `${pct}%` }}
            data-testid={`upload-progress-${job.id}`}
          />
        </div>
        <div className="text-[10px] text-ink-soft mt-1 flex items-center gap-2">
          <span>{kb(job.uploaded_bytes)} / {kb(job.size)}</span>
          <span className="tabular-nums">· {pct}%</span>
          {job.total_chunks > 1 && (
            <span>· {job.received_chunks.length}/{job.total_chunks} chunks</span>
          )}
          {job.error && <span className="text-[#B94A48] truncate">· {job.error}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {job.status === "uploading" && (
          <button
            data-testid={`pause-${job.id}`}
            onClick={() => q.pause(job.id)}
            title="Pause"
            className="w-7 h-7 rounded-full border border-[#E8EAE9] grid place-items-center text-ink-mid hover:bg-linen"
          >
            <Pause className="w-3.5 h-3.5" />
          </button>
        )}
        {(job.status === "paused" || job.status === "error") && (
          <button
            data-testid={`resume-${job.id}`}
            onClick={() => q.resume(job.id)}
            title="Resume"
            className="w-7 h-7 rounded-full border border-[#E8EAE9] grid place-items-center text-ink-mid hover:bg-linen"
          >
            <Play className="w-3.5 h-3.5" />
          </button>
        )}
        {["uploading", "paused", "queued", "error"].includes(job.status) && (
          <button
            data-testid={`abort-${job.id}`}
            onClick={() => q.abort(job.id)}
            title="Cancel"
            className="w-7 h-7 rounded-full border border-[#F0B0AE] grid place-items-center text-[#B94A48] hover:bg-[#FFF3F3]"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {["completed", "aborted"].includes(job.status) && (
          <button
            data-testid={`clear-${job.id}`}
            onClick={() => q.clear(job.id)}
            className="text-[10px] px-2 py-1 rounded-full text-ink-soft hover:text-ink"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}


/* ============================================================================ */
/* Small components                                                             */
/* ============================================================================ */

function StatCell({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">{label}</div>
      <div className="font-display text-2xl text-ink tabular-nums">{value}</div>
      <div className="text-[10px] text-ink-soft mt-0.5">{sub}</div>
    </div>
  );
}


function FileCard({ f, onOpen, onDelete, onInfo, onToggleFav, isFav, compareMode, isSelected }) {
  const type = f._type;
  const meta = TYPE_META[type] || TYPE_META.other;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`file-card-${f.id}`}
      className={`group relative rounded-2xl overflow-hidden bg-white border transition-all duration-200 ${
        isSelected ? "border-pine ring-2 ring-pine/20 shadow-md" : "border-[#EFEADC] hover:border-pine/30 hover:shadow-lg hover:-translate-y-0.5"
      }`}
    >
      <button
        onClick={onOpen}
        data-testid={`file-open-${f.id}`}
        className="w-full aspect-square bg-gradient-to-br from-linen/60 to-white grid place-items-center relative"
      >
        <Icon className="w-9 h-9 text-ink-soft group-hover:text-ink-mid transition-colors" />
        <span className={`absolute top-2.5 left-2.5 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.chip}`}>
          {meta.label}
        </span>
        {compareMode && ["image", "heic"].includes(type) && (
          <div className={`absolute top-2.5 right-2.5 w-5 h-5 rounded-md border-2 grid place-items-center ${isSelected ? "bg-pine border-pine" : "bg-white border-pine/40"}`}>
            {isSelected && <Check className="w-3 h-3 text-linen" />}
          </div>
        )}
      </button>

      <div className="p-3">
        <div className="text-sm font-medium text-ink truncate" title={f.original_filename}>
          {f.original_filename}
        </div>
        <div className="text-[10px] text-ink-soft mt-1 flex items-center justify-between">
          <span>{fmtDate(f.created_at)}</span>
          <span className="tabular-nums">{kb(f.size)}</span>
        </div>
        {f.uploaded_by_name && (
          <div className="text-[10px] text-ink-soft mt-0.5 truncate">{f.uploaded_by_name}</div>
        )}
      </div>

      {!compareMode && (
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            data-testid={`fav-${f.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
            className={`w-7 h-7 rounded-full bg-white/90 border border-[#EFEADC] grid place-items-center transition-colors ${isFav ? "text-[#D0A24C]" : "text-ink-soft hover:text-[#D0A24C]"}`}
            title={isFav ? "Remove from favourites" : "Favourite"}
          >
            <Star className={`w-3.5 h-3.5 ${isFav ? "fill-current" : ""}`} />
          </button>
          <button
            data-testid={`info-${f.id}`}
            onClick={(e) => { e.stopPropagation(); onInfo(); }}
            className="w-7 h-7 rounded-full bg-white/90 border border-[#EFEADC] grid place-items-center text-ink-mid hover:text-ink transition-colors"
            title="File info"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid={`del-${f.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="w-7 h-7 rounded-full bg-white/90 border border-[#EFEADC] grid place-items-center text-ink-mid hover:text-[#B94A48] hover:border-[#F0B0AE] transition-colors"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {isFav && !compareMode && (
        <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/90 border border-[#EFEADC] grid place-items-center text-[#D0A24C] pointer-events-none group-hover:opacity-0 transition-opacity">
          <Star className="w-3.5 h-3.5 fill-current" />
        </div>
      )}
    </div>
  );
}


function EmptyState({ onUpload }) {
  return (
    <div className="rounded-3xl border border-dashed border-[#E8EAE9] bg-gradient-to-b from-linen/40 to-white py-16 md:py-20 text-center px-6" data-testid="pf-empty-state">
      <div className="w-24 h-24 mx-auto rounded-full bg-white border border-[#EFEADC] grid place-items-center mb-6 relative">
        <ScanLine className="w-10 h-10 text-ink-soft" />
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white border border-[#EFEADC] grid place-items-center">
          <Sparkles className="w-3.5 h-3.5 text-ink-soft" />
        </div>
      </div>
      <h3 className="font-display text-2xl text-ink">No imaging or documents yet</h3>
      <p className="text-sm text-ink-mid mt-2 max-w-md mx-auto">
        Upload DICOM studies, TIFF images, X-rays, prescriptions or reports for this patient. Anything you add here will appear in the timeline too.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          data-testid="empty-upload"
          onClick={onUpload}
          className="btn-primary text-xs inline-flex items-center gap-2"
        >
          <Upload className="w-3.5 h-3.5" /> Upload files
        </button>
        <a
          href="https://viyora.co.in/help/imaging"
          target="_blank" rel="noreferrer"
          className="text-xs px-4 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen inline-flex items-center gap-2"
          data-testid="empty-learn-more"
        >
          Learn more
        </a>
      </div>
    </div>
  );
}


/* ============================================================================ */
/* Preview viewer — routes to per-type renderer                                  */
/* ============================================================================ */

function FileViewer({ viewer, onClose, onInfo }) {
  const { file } = viewer;
  const type = file._type;

  if (type === "image" || type === "heic") {
    return <ImageViewer viewer={viewer} onClose={onClose} onInfo={onInfo} />;
  }
  if (type === "tiff") {
    // Browsers cannot render TIFF natively — show metadata + download.
    return <NonPreviewViewer viewer={viewer} onClose={onClose} onInfo={onInfo}
      title="TIFF image"
      hint="TIFF files preview as a full download. Zoom and rotate become available once the DICOM viewer ships." />;
  }
  if (type === "dicom") {
    return <NonPreviewViewer viewer={viewer} onClose={onClose} onInfo={onInfo}
      title="DICOM study"
      hint="This DICOM study is ready. Download the original to open in your DICOM software." />;
  }
  if (type === "pdf") {
    return <PDFViewer viewer={viewer} onClose={onClose} onInfo={onInfo} />;
  }
  return <NonPreviewViewer viewer={viewer} onClose={onClose} onInfo={onInfo}
    title="File"
    hint="Preview is not available for this file type. You can still download the original." />;
}


function IconBtn({ children, onClick, tip, testid, active }) {
  return (
    <button
      type="button" onClick={onClick} title={tip} data-testid={testid}
      className={`w-9 h-9 rounded-lg grid place-items-center transition-colors text-white ${active ? "bg-white/20" : "hover:bg-white/10"}`}
    >
      {children}
    </button>
  );
}


function ViewerChrome({ file, onClose, onInfo, actions }) {
  const type = file._type;
  const meta = TYPE_META[type] || TYPE_META.other;
  return (
    <div className="flex items-center justify-between px-5 py-3 bg-black/60 text-white text-sm">
      <div className="min-w-0">
        <div className="truncate font-medium" data-testid="viewer-filename">{file.original_filename}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
          <span className={`px-1.5 py-0.5 rounded-full ${meta.chip} text-[9px]`}>{meta.label}</span>
          <span className="ml-2">{fmtDate(file.created_at)} · {kb(file.size)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {actions}
        {onInfo && (
          <IconBtn tip="File info" testid="viewer-info" onClick={onInfo}><Info className="w-4 h-4" /></IconBtn>
        )}
        <IconBtn tip="Close" testid="viewer-close" onClick={onClose}><X className="w-4 h-4" /></IconBtn>
      </div>
    </div>
  );
}


function ImageViewer({ viewer, onClose, onInfo }) {
  const { url, file } = viewer;
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dims, setDims] = useState(null);
  const dragRef = useRef(null);

  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = file.original_filename || "image";
    a.click();
  };

  const onWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.min(6, Math.max(0.5, z + (e.deltaY < 0 ? 0.2 : -0.2))));
  };
  const onMouseDown = (e) => { dragRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onMouseMove = (e) => {
    if (!dragRef.current || zoom === 1) return;
    setPos({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onMouseUp = () => { dragRef.current = null; };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="file-viewer-image">
      <ViewerChrome
        file={{ ...file, _resolution: dims }} onClose={onClose} onInfo={onInfo}
        actions={
          <>
            <IconBtn tip="Zoom out" testid="btn-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}><ZoomOut className="w-4 h-4" /></IconBtn>
            <span className="text-[11px] text-white/70 min-w-[52px] text-center font-mono" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
            <IconBtn tip="Zoom in" testid="btn-zoom-in" onClick={() => setZoom((z) => Math.min(6, z + 0.2))}><ZoomIn className="w-4 h-4" /></IconBtn>
            <IconBtn tip="Rotate" testid="btn-rotate" onClick={() => setRotation((r) => (r + 90) % 360)}><RotateCw className="w-4 h-4" /></IconBtn>
            <IconBtn tip="Reset" testid="btn-zoom-reset" onClick={() => { setZoom(1); setRotation(0); setPos({ x: 0, y: 0 }); }}><Maximize2 className="w-4 h-4" /></IconBtn>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <IconBtn tip="Download" testid="btn-download" onClick={download}><Download className="w-4 h-4" /></IconBtn>
          </>
        }
      />
      <div
        className="flex-1 overflow-hidden grid place-items-center cursor-move"
        onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      >
        <img
          src={url}
          alt={file.original_filename}
          draggable={false}
          data-testid="viewer-image"
          onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) rotate(${rotation}deg) scale(${zoom})`,
            transition: dragRef.current ? "none" : "transform .18s ease-out",
            maxHeight: "84vh", maxWidth: "90vw", userSelect: "none",
          }}
          className="rounded shadow-2xl"
        />
      </div>
      <div className="px-4 py-2 text-center text-[10px] uppercase tracking-widest text-white/40">
        Scroll to zoom · Drag to pan · R to rotate
      </div>
    </div>
  );
}


function PDFViewer({ viewer, onClose, onInfo }) {
  const { url, file } = viewer;
  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = file.original_filename || "document.pdf";
    a.click();
  };
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="file-viewer-pdf">
      <ViewerChrome
        file={file} onClose={onClose} onInfo={onInfo}
        actions={<IconBtn tip="Download" testid="btn-download" onClick={download}><Download className="w-4 h-4" /></IconBtn>}
      />
      <div className="flex-1 bg-black/70 p-4">
        <iframe
          data-testid="pdf-iframe"
          title={file.original_filename}
          src={url}
          className="w-full h-full rounded-lg bg-white"
        />
      </div>
    </div>
  );
}


function NonPreviewViewer({ viewer, onClose, onInfo, title, hint }) {
  const { url, file } = viewer;
  const type = file._type;
  const meta = TYPE_META[type] || TYPE_META.other;
  const Icon = meta.icon;
  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = file.original_filename || `download.${type}`;
    a.click();
  };
  const isDicom = type === "dicom";
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid={`file-viewer-${type}`}>
      <ViewerChrome file={file} onClose={onClose} onInfo={onInfo} actions={null} />
      <div className="flex-1 grid place-items-center p-6">
        <div className="max-w-lg text-center rounded-3xl bg-white/5 border border-white/10 p-10 backdrop-blur-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-white/10 grid place-items-center mb-5">
            <Icon className="w-10 h-10 text-white/70" />
          </div>
          <div className="font-display text-2xl text-white">{title}</div>
          <p className="text-sm text-white/60 mt-2">{hint}</p>
          {isDicom && (
            <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-4 text-left text-xs text-white/70 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Preview metadata</div>
              <MetaRow k="Filename" v={file.original_filename} />
              <MetaRow k="Size" v={kb(file.size)} />
              <MetaRow k="Uploaded" v={fmtDateTime(file.created_at)} />
              {file.uploaded_by_name && <MetaRow k="Doctor" v={file.uploaded_by_name} />}
            </div>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={download} data-testid="btn-download-original"
              className="text-xs px-4 py-2 rounded-full bg-white text-ink font-medium inline-flex items-center gap-2 hover:bg-white/90"
            >
              <Download className="w-3.5 h-3.5" /> Download original
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function MetaRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-white/40 uppercase tracking-widest text-[9px]">{k}</span>
      <span className="truncate max-w-[240px] text-right">{v}</span>
    </div>
  );
}


function MetaDrawer({ file, patientName, onClose }) {
  const type = file._type;
  const meta = TYPE_META[type] || TYPE_META.other;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-w-md" data-testid="meta-drawer">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Info className="w-4 h-4 text-ink-mid" /> File details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-2xl bg-linen/60 border border-[#EFEADC] p-3">
            <div className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${meta.chip}`}>{meta.label}</div>
            <div className="truncate text-sm font-medium text-ink">{file.original_filename}</div>
          </div>

          <div className="divide-y divide-[#F0EBE0] text-sm">
            <MetaLine k="Filename" v={file.original_filename} />
            <MetaLine k="Upload date" v={fmtDateTime(file.created_at)} />
            <MetaLine k="Doctor" v={file.uploaded_by_name || "—"} />
            <MetaLine k="Patient" v={patientName || "—"} />
            <MetaLine k="File size" v={kb(file.size)} />
            <MetaLine k="Type" v={file.content_type} mono />
            <MetaLine k="Storage path" v={file.storage_path?.split("/").slice(-1)[0]} mono />
          </div>

          {type === "dicom" && (
            <div className="rounded-2xl border border-[#EFEADC] bg-linen/40 p-3 text-xs">
              <div className="overline text-ink-soft mb-1.5">DICOM tags</div>
              <p className="text-ink-mid">
                Study date, modality, manufacturer and series description will appear here once the DICOM viewer integration is enabled.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MetaLine({ k, v, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <span className="text-[10px] uppercase tracking-widest text-ink-soft shrink-0">{k}</span>
      <span className={`text-right text-ink/80 truncate ${mono ? "font-mono text-[11px]" : ""}`}>{v || "—"}</span>
    </div>
  );
}


/* ============================================================================ */
/* CompareViewer (preserved from previous version)                              */
/* ============================================================================ */

function CompareViewer({ items, onClose }) {
  const cols = Math.min(items.length, 4);
  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="xray-compare">
      <div className="flex items-center justify-between px-5 py-3 bg-black/60 text-white text-sm">
        <div>
          <div className="font-medium">Comparing {items.length} images</div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            Oldest first · left to right shows progression over time
          </div>
        </div>
        <IconBtn tip="Close" testid="btn-close-compare" onClick={onClose}><X className="w-4 h-4" /></IconBtn>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <div className="grid gap-4 h-full" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {items.map((it, idx) => (
            <div key={it.file.id} className="flex flex-col min-h-0" data-testid={`compare-slot-${idx}`}>
              <div className="text-white/80 text-xs mb-2 flex items-center justify-between">
                <div>
                  <span className="font-medium">{fmtDate(it.file.created_at)}</span>
                  <span className="ml-2 text-white/50">{it.file.original_filename}</span>
                </div>
                <a href={it.url} download={it.file.original_filename} data-testid={`btn-cmp-download-${idx}`} className="text-white/60 hover:text-white">
                  <Download className="w-3.5 h-3.5" />
                </a>
              </div>
              <div className="flex-1 bg-black/40 rounded-lg overflow-hidden grid place-items-center min-h-[300px]">
                <img src={it.url} alt={it.file.original_filename} data-testid={`compare-img-${idx}`} className="max-w-full max-h-full object-contain" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
